/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { Brand } from '@/lib/brand/brand';

/**
 * Per-brand composition for the header store control.
 *
 * A brand whose arm is `null` renders NOTHING, which is how BG gets no control
 * without `header-store-link.tsx` knowing BG exists. NO BRAND BRANCH LIVES IN
 * THE COMPONENT — it only reads this map, the same shape the PDP fulfilment
 * composition uses.
 *
 * ── WHAT LIVE RENDERS, AND HOW IT WAS ESTABLISHED (2026-08-22) ──
 *
 * Live NM's desktop header carries a small underlined control at the top-left,
 * on its own line ABOVE the WOMEN / MEN tabs, mirroring My Orders on the right.
 * It is the FIRST element of the header's left region: store control, then the
 * gender tabs, then the brand logo.
 *
 * It is a `button`, not a link. It has no href and navigates nowhere; it opens
 * live's own side panel at its store-list page. Ours opens the store locator
 * this storefront already ships, which is the same KIND of destination — a
 * panel listing stores you can choose from — and not a reconstruction of
 * live's, whose contents no capture on disk contains.
 *
 * TWO STATES. Live's own production bundle carries the fallback verbatim:
 *
 *     "aria-label": Y || "Our Stores"   ... children: Y || "Our Stores"
 *
 * so `Y` is the store name and "Our Stores" is what renders without one. Title
 * case, one space, no ampersand. NM's Tealium config pins the same string in
 * its analytics taxonomy, which is an independent source for the copy.
 *
 * EVIDENCE TIERS, and note which one settled which question:
 *
 *   - `.reference/dumps/nm-*` — live page saves taken POST-HYDRATION that keep
 *     live's semantic class names. `div.hide-on-mobile.nm-new-header-container`
 *     holding one `button[type=button]`, in 13 of 13 NM dumps and 0 of 14 BG
 *     dumps. Rendered labels: "Denver" in 11, "Beverly Hills" in 2.
 *   - the decompiled captures resolve its computed styling:
 *     `text-[12px] tracking-[0.5px] leading-[36px]` underlined, transparent
 *     ground, `neuzeit-grotesk`, at `home-d-1280` line 94 and `search-d-1280`
 *     line 96. Live's own header stylesheet declares the same five values, so
 *     the type is agreed across two independent tiers.
 *   - the owner's live screenshots, provenance read from the address bar INSIDE
 *     each image: `nm-search-1024.png` and `nm-search-1280.png`, address bar
 *     `neimanmarcus.com`, show "Denver" underlined at the top-left above the
 *     tabs.
 *
 * THE SSR DUMPS CANNOT ANSWER THIS AND WILL SAY "NO". The control is injected
 * client-side, so `nm-new-header-container` is absent from every Aug-15 raw
 * `.html` save while `clean-header-section` is present in the same file. A
 * search of only those files returns a confident zero. That is very likely how
 * this element came to be recorded as one live does not have.
 *
 * ── WHAT IS DELIBERATELY NOT COPIED ──
 *
 * Live positions it `absolute` at `top-[10px]` with a `left` that steps per
 * page and per width, and pads the line to `leading-[36px]`. None of that is
 * carried here: it is the box-model noise of an absolutely-positioned element,
 * the same class of value as the `mr-30` already dropped from My Orders. Ours
 * sits in normal flow at the top of the left zone, which puts its ink at the
 * 30px gutter — live's own ink lands at 31px (`left-[25px]` plus its
 * `pl-[6px]`), so the relation the picture shows is reproduced without
 * importing the mechanism.
 *
 * The TYPE is copied, and it is the same treatment live gives My Orders. Live's
 * mobile nav drawer renders "Denver" and "My Orders" as adjacent rows sharing
 * one class string, which is direct evidence that the two are one role rather
 * than two lookalikes.
 *
 * ── DESKTOP ONLY, AND THAT IS MEASURED, NOT ASSUMED ──
 *
 * Live SHIPS the markup at 390 and 440 and switches it off:
 * `hide-on-mobile` resolves to `display:none` under a `max-width:767px` media
 * query, and the post-hydration captures at 375, 390 and 440 record no store
 * control at all against `div` controls of 1280-1352. Live relocates it into
 * the slide-out nav drawer instead. Reproducing the drawer row is a separate
 * piece of work and is NOT done here.
 *
 * ── BG HAS NONE. DO NOT PORT. ──
 *
 * Proven at three tiers, each with a positive control in the same command:
 *
 *   - live bundles: `nm-new-header-container`, `storeListPage`,
 *     `align-store-link` and `handleStoreClick` all return 0 across the BG
 *     dumps against a `createElement` control of 41,497.
 *   - enumerated DOM, container found and read past its close: BG's header row
 *     is three equal thirds — the "Open search panel" control, the wordmark,
 *     then Sign in / Wishlist / Bag. Complete, in `bg-home-d-1280` lines 71-116
 *     and in the BG homepage save's `header-container`. BG's top-left is its
 *     SEARCH trigger, not a store control.
 *   - the owner's live BG screenshot, address bar `bergdorfgoodman.com`, shows
 *     no store element anywhere in the header.
 *
 * ONE DECOY, recorded so it is not re-derived as a finding: `Our Stores` DOES
 * appear in BG's pages, twice. Once in `utag.js`, which is shared Tealium
 * extension code deployed to both brands and never rendered — on BG it is a
 * branch that cannot fire, because BG has no element able to emit it. Once as
 * prose inside a loyalty JSON blob describing gift cards. Neither is a link.
 */
export interface HeaderStoreLinkComposition {
    /**
     * Class string for the control itself. COMPLETE and static — never composed
     * from a constant, because Tailwind's scanner reads source text and a class
     * assembled at runtime lands on the element while emitting no rule.
     *
     * The My Orders link's own string, with two element-scoped differences,
     * both of them live's own measured values for THIS element:
     *
     *   - tracking is the `0.5px` literal rather than the token My Orders
     *     names. The value is identical; pointing this element at a token named
     *     for another one would assert a shared decision nobody made.
     *   - `leading-[36px]` is live's line box. It is the ONLY thing separating
     *     this control from the tabs below it — live's own separation is not a
     *     margin or a gap but exactly this leading around 12px text, with the
     *     control's box bottom meeting the tab container's top. Reproducing the
     *     leading rather than inventing a gap keeps every number here measured,
     *     and because the column is bottom-aligned the extra line height grows
     *     UPWARD and cannot move the tabs.
     */
    className: string;

    /**
     * Leading text stripped from the selected store's name before it is
     * rendered, when the name actually starts with it.
     *
     * Live NM shows the LOCATION alone. The evidence is already in this file:
     * its post-hydration dumps render "Denver" in 11 of 13 and "Beverly Hills"
     * in 2 — never the branded record name. Our own catalogue stores it the
     * other way: all 33 imported NM records are named "Neiman Marcus - <place>"
     * (verified against the imported stores.xml), so the header was rendering
     * "Neiman Marcus - Denver" where live renders "Denver".
     *
     * STRIP THE PREFIX; DO NOT SPLIT ON THE SEPARATOR. Eight of the 33 carry a
     * second one — "Neiman Marcus - Newport Beach - Fashion Island",
     * "Neiman Marcus - Dallas - Downtown" — so taking the text after the LAST
     * separator yields "Fashion Island" and "Downtown", dropping the city.
     * Removing the prefix yields "Newport Beach - Fashion Island", which is
     * what a shopper calls that store.
     *
     * The store record's own `city` field cannot be used: the pre-import export
     * carries "Texas" as the city on two Dallas-area records, and
     * `SelectedStoreInfo` does not carry city at all — widening it would ripple
     * through six callers across the locator and BOPIS for a label change.
     *
     * DEGRADES CORRECTLY BY CONSTRUCTION: a name that does not start with this
     * prefix is returned unchanged, so an unprefixed or differently-named
     * record renders exactly as it does today rather than being truncated.
     * A brand whose arm is `null` never reaches this at all.
     */
    storeNamePrefix?: string;
}

export const HEADER_STORE_LINK_BY_BRAND: Record<Brand, HeaderStoreLinkComposition | null> = {
    nm: {
        /*
         * `py-px pl-4 mb-[10px]` — THE THREE VALUES THAT REVERSE THE "NOT
         * COPIED" PARAGRAPH ABOVE, and the reversal is the owner's, not mine.
         *
         * That paragraph declined live's `top:10px` and its `left` as "the
         * box-model noise of an absolutely-positioned element". The owner's
         * 2026-08-22 flicker test named the consequence directly: "our stores
         * link sits way too low." It was, by 11px. So the mechanism stays
         * declined — we are still in normal flow, not absolute — but the
         * RESULTING INK POSITION is now reproduced, which is what the picture
         * shows and what the paragraph was trying to achieve.
         *
         * The paragraph's horizontal figure was also read off the wrong tier.
         * It cites `left-[25px]` plus `pl-[6px]` for ink at 31. Live's header
         * bundle declares THREE arms:
         *
         *     .nm-new-header-container                        { left: 40px }
         *     .nm-new-header-container.align-store-link       { left: 25px }
         *     @media (min-width:768px) and (max-width:1023px) { left: 25px }
         *
         * so 25 is the TABLET tier and the `align-store-link` variant. All five
         * NM desktop page saves under .reference/live-shots/owner-2026-08-22/
         * carry the PLAIN class — `align-store-link` occurs 0 times in every one
         * of them — so at >=1024 live is `left:40px`, and with the button's own
         * `padding:1px 6px` its ink lands at 46, not 31. Ours sat at 30.
         *
         * Measured by rendering designers-nm-1280.html (the signed-out save) at
         * clientWidth 1280 with JS off:
         *
         *                      live      ours(before)   ours(after)
         *     ink x             46          30             46
         *     box y, rel        10          22             10
         *     text centre, rel  29          40             29
         *     box h             38          36             38
         *
         * `mb-[10px]` rather than a negative top offset because the column is
         * bottom-aligned — the paragraph above says so explicitly — so box
         * growth travels UPWARD and the gender tabs below cannot move. Verified:
         * the tabs stay at y=108 before and after.
         *
         * `py-px` reproduces live's `padding:1px 6px` vertical half only. The
         * 6px horizontal was folded into `pl-4` (16px = the 46 ink target minus
         * our 30px gutter) rather than carried separately, because our gutter
         * and live's `left` are different mechanisms reaching the same ink.
         *
         * `pl-4` REMOVED 2026-08-22, and the reason is a conflict the earlier
         * pass could not see. Owner, flicker round 2: "stores link should be
         * flush on the left with the gender nav, mirroring how the Orders link
         * is right-flush with bag and search on the top right; ours is too far
         * right."
         *
         * He is describing live correctly. On live BOTH inks start at x=46:
         * the store control is `.nm-new-header-container` at `left:40px` with
         * the button's own `padding:1px 6px`, and the gender button sits at 46
         * with no padding of its own. Measured by rendering
         * .reference/live-shots/owner-2026-08-22/designers-nm-1280.html at
         * clientWidth 1280 with JS disabled — store button box x=40, gender
         * button box x=46, so 40 + 6 = 46 on both.
         *
         * OURS COULD ONLY REPRODUCE ONE OF THE TWO. Our gender tab ink sits at
         * the header row's own content origin (30 at 1280, 94 at 1728), and
         * moving it to live's 46 is a separate, owner-blocked change. The 16px
         * of `pl-4` put the STORE ink on live's absolute 46 while the gender
         * nav stayed at the row origin — absolute parity for one element,
         * bought by breaking the relationship between the two that the owner
         * can actually see when he flickers.
         *
         * Dropping it makes them flush BY CONSTRUCTION rather than by a number:
         * both controls are children of the same header row and now both start
         * at its content origin, so no value has to be kept in sync. When the
         * gender left-origin change lands, the store link follows it to 46
         * without anyone editing this file — which is the whole point of
         * removing the offset rather than retuning it.
         *
         * Vertical is untouched: `py-px` and `mb-[10px]` are unchanged, and
         * padding-left cannot reach them. The owner calls this control's
         * vertical position perfect as of this round, so that had to hold.
         */
        className:
            'hidden md:inline-block type-label-small leading-[36px] tracking-[0.5px] whitespace-nowrap underline cursor-pointer focus-ring-button hover:opacity-50 transition-opacity py-px mb-[10px]',
        storeNamePrefix: 'Neiman Marcus - ',
    },
    bg: null,
};
